import type { ChartData } from 'chart.js';
import { useMemo } from 'react';

import {
  CHART_GRAPH,
  CHART_TOOLTIP_TARGET_TYPE,
} from '@/entities/chart/model/constants';
import useChartColor from '@/features/(charts)/chart-renderer/hooks/use-chart-color';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import useColor from '@/hooks/domain/useColor';
import { getAlphaColor } from '@/styles/colors/color.utils';

type Props = {
  chart: ChartBase;
  tableData: ChartTableRes;
};

const useChartAccData = (
  props: Props,
): {
  accChartData: ChartData<'bar' | 'doughnut'>;
  labels?: string[];
} => {
  const color = useColor();
  const { chart, tableData } = props;
  const getLabel = useChartLabel(tableData.field_item_meta);
  const getColor = useChartColor(chart.field);

  const singleTableData: ChartSingleTableRes = chartUtils.getSortedRowItems(
    chart,
    tableData,
    chart.selected_field.items || [],
  ) as ChartSingleTableRes;

  const labels = useMemo(() => {
    return singleTableData.tables[0]?.map((segment) =>
      getLabel(chart.field.type, segment.x),
    );
  }, [chart.field.type, getLabel, singleTableData.tables]);

  const accChartData = useMemo(() => {
    const result = {
      labels: labels,
      datasets: [
        {
          label: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          data:
            singleTableData.tables[0]?.map((row) => {
              return row?.value || 0;
            }) || [],
          backgroundColor:
            singleTableData.tables[0]?.map((row) => {
              return getColor(row?.x);
            }) || [],
          meta: singleTableData.tables[0]?.map((row) => row.x),
          date_type: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          borderColor: color.gray100,
          borderWidth: 1,
        },
      ],
    };

    if (chart.target.is_compare) {
      const compareDataset = {
        label: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
        data:
          singleTableData.tables[0]?.reduce((acc, value) => {
            const data = singleTableData.compare_tables?.[0]?.find(
              (r) => r.x === value.x,
            );
            if (data) acc.push(data?.value || 0);
            return acc;
          }, [] as number[]) || [],
        backgroundColor:
          singleTableData.tables[0]?.reduce((acc, row) => {
            const data = singleTableData.compare_tables?.[0]?.find(
              (r) => r.x === row.x,
            );
            if (data) {
              acc.push(getAlphaColor(getColor(data?.x), 0.6));
            }
            return acc;
          }, [] as string[]) || [],
        meta: singleTableData.tables[0]?.map((row) => row.x),
        date_type: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
        borderColor: color.gray100,
        borderWidth: 1,
      };
      if (chart.graph === CHART_GRAPH.BAR) {
        result.datasets.unshift(compareDataset);
      } else {
        result.datasets.push(compareDataset);
      }
    }

    return result;
  }, [
    chart.graph,
    chart.target.is_compare,
    color.gray100,
    getColor,
    labels,
    singleTableData.compare_tables,
    singleTableData.tables,
  ]);

  return {
    accChartData,
    labels,
  };
};

export default useChartAccData;
